/**
 * @fileoverview Main game orchestration for toddler drag-and-drop game.
 * Initializes Konva stage, manages game state, and coordinates all subsystems.
 */

import { CONFIG } from './GameConfig.js';
import { ShapeFactory } from '../factories/ShapeFactory.js';
import { DragHandlers } from './DragHandlers.js';
import { GameUtils, MatchingPair } from '../utilities/GameUtils.js';
import { AudioSystem } from '../services/AudioSystem.js';
import { GameStateService } from '../services/GameStateService.js';
import { AnimationEffects } from '../utilities/Animations.js';
import { ThemeService } from '../services/ThemeService.js';
import { MovementAnimationManager } from '../utilities/MovementAnimationManager.js';
import { PremiumService } from '../services/PremiumService.js';
import { DebugService } from '../services/DebugService.js';
import { StageManager } from './StageManager.js';
import { setupRouting } from './routing.js';
import Konva from 'konva';
import { CustomNode } from '../konva-extensions.js';
import { BaseTheme } from '../themes/abstract/BaseTheme.js';
import { addClickAndTouch, removeClickAndTouch } from '../utilities/EventUtils.js';
import { mountRotateOverlay } from '../components/RotateOverlay.js';
import { mountStartOverlay } from '../components/StartOverlay.js';
import { mountUnlockModal } from '../components/UnlockModal.js';
import { mountMenuDropdown } from '../components/MenuDropdown.js';
import { themeNames } from '../i18n/themes.js';
//import { StorageService } from '../services/StorageService.js';

/**
 * Main game class for the toddler drag-and-drop game.
 * Manages Konva stage, layers, game state, and audio.
 */
export class ToddlerGame {
    stageManager: StageManager;
    stage: Konva.Stage | null = null;
    staticLayer: Konva.Layer | null = null;
    dragLayer: Konva.Layer | null = null;
    targets: CustomNode[] = [];
    draggables: CustomNode[] = [];
    feedbackText: Konva.Text | null = null;
    completedCount: number = 0;
    matchingPairs: MatchingPair[] = [];
    dragHandlers: DragHandlers;
    audioSystem: AudioSystem;
    gameState: GameStateService;
    hamburgerButton: HTMLElement | null = null;
    menuDropdown: HTMLElement | null = null;
    muteButton: HTMLElement | null = null;
    muteIcon: HTMLElement | null = null;
    darkThemeButton: HTMLElement | null = null;
    isDarkTheme: boolean = false;
    themeNameElement: HTMLElement | null = null;
    toggleMuteHandler: ((evt: any) => void) | null = null;
    toggleMenuHandler: ((evt: any) => void) | null = null;
    toggleDarkThemeHandler: ((evt: any) => void) | null = null;
    resizeTimeout: any = null;
    currentTheme: BaseTheme | null = null;
    isPaused: boolean = false;
    movementManager: MovementAnimationManager | null = null;
    isPremiumUser: boolean = false;
    // singleton instance 
    static game: ToddlerGame;
    
    constructor() {
        this.stageManager = new StageManager('container');
        this.gameState = new GameStateService();
        this.dragHandlers = new DragHandlers(this);
        this.audioSystem = new AudioSystem();
        
        // Check premium status
        const premiumStatus = PremiumService.checkPremiumStatus();
        this.isPremiumUser = premiumStatus.isPremiumUser;
        console.log('Premium status:', this.isPremiumUser);
        
        // Handle Stripe redirect (payment success)
        this.handleStripeRedirect();

        this.setupStateListeners();
        this.setupVisibilityHandling();
        this.init();
        this.setupResizeHandler();
    }

    // get singleton instance
    static getInstance(): ToddlerGame {
        if (!ToddlerGame.game) {
            ToddlerGame.game = new ToddlerGame();
        }
        return ToddlerGame.game;
    }
    
    /**
     * Handle Stripe redirect after payment.
     * Checks URL parameters for success=true and session_id.
     */
    async handleStripeRedirect() {
        const urlParams = new URLSearchParams(window.location.search);
        const success = urlParams.get('success');
        const sessionId = urlParams.get('session_id');
        
        if (success === 'true' && sessionId) {
            console.log('Payment successful! Verifying session...');
            
            try {
                const unlockCode = await PremiumService.handlePaymentSuccess(sessionId);
                
                if (unlockCode) {
                    console.log('Premium unlocked! Code:', unlockCode);
                    this.isPremiumUser = true;
                    
                    // Show success message to user
                    alert(`Premium unlocked! 🎉\n\nYour unlock code: ${unlockCode}\n\nSave this code to restore premium access on other devices.`);
                    
                    // Clean up URL
                    window.history.replaceState({}, document.title, window.location.pathname);
                    
                    // Reload to show premium themes
                    window.location.reload();
                } else {
                    console.error('Payment verification failed: No unlock code received');
                    alert('Payment verification failed. Please contact support.');
                }
            } catch (error) {
                console.error('Error verifying payment:', error);
                alert('Error verifying payment. Please contact support.');
            }
        }
    }
    
    /**
     * Setup premium UI elements (badge, unlock button in menu, modal)
     */
    setupPremiumUI() {
        const premiumBadge = document.getElementById('premiumBadge');
        const unlockButton = document.getElementById('unlockButton');
        const restorePurchase = document.getElementById('restorePurchase');
        const unlockModal = document.getElementById('unlockModal');
        const unlockModalCancel = document.getElementById('unlockModalCancel');
        const unlockModalSubmit = document.getElementById('unlockModalSubmit');
        const unlockCodeInput = document.getElementById('unlockCodeInput') as HTMLInputElement;
        
        // Show/hide elements based on premium status
        if (this.isPremiumUser) {
            premiumBadge?.classList.add('show');
            unlockButton?.classList.add('hidden');
            restorePurchase?.classList.add('hidden');
        } else {
            premiumBadge?.classList.remove('show');
            // remove butteons for now since premium is not available, but keep code in place for future re-enabling
            unlockButton?.classList.add('hidden');
            restorePurchase?.classList.add('hidden');
        }
        
        // Unlock button - start Stripe checkout
        const handleUnlock = async (e?: Event) => {
            if (e && e.type === 'touchend') e.preventDefault();
            this.closeMenu();
            console.log('Starting checkout...');
            await PremiumService.initStripeCheckout();
        };
        if (unlockButton) addClickAndTouch(unlockButton, handleUnlock);
        
        // Restore purchase link - show modal
        const handleRestore = (e?: Event) => {
            if (e && e.type === 'touchend') e.preventDefault();
            this.closeMenu();
            unlockModal?.classList.add('show');
            unlockCodeInput?.focus();
        };
        if (restorePurchase) addClickAndTouch(restorePurchase, handleRestore);
        
        // Modal cancel button
        const handleCancel = (e?: Event) => {
            if (e && e.type === 'touchend') e.preventDefault();
            unlockModal?.classList.remove('show');
            if (unlockCodeInput) {
                unlockCodeInput.value = '';
            }
        };
        if (unlockModalCancel) addClickAndTouch(unlockModalCancel, handleCancel);
        
        // Modal submit button - validate code
        const handleSubmit = async (e?: Event) => {
            if (e && e.type === 'touchend') e.preventDefault();
            const code = unlockCodeInput?.value.trim().toUpperCase();
            
            if (!code) {
                alert('Please enter an unlock code');
                return;
            }
            
            console.log('Validating code:', code);
            const isValid = await PremiumService.validateUnlockCode(code);
            
            if (isValid) {
                this.isPremiumUser = true;
                unlockModal?.classList.remove('show');
                alert('Premium unlocked! 🎉\n\nReloading game...');
                window.location.reload();
            } else {
                alert('Invalid unlock code. Please check and try again.');
                if (unlockCodeInput) {
                    unlockCodeInput.value = '';
                    unlockCodeInput.focus();
                }
            }
        };
        if (unlockModalSubmit) addClickAndTouch(unlockModalSubmit, handleSubmit);
        
        // Auto-format unlock code input (add dashes)
        unlockCodeInput?.addEventListener('input', (e) => {
            const input = e.target as HTMLInputElement;
            let value = input.value.replace(/[^A-F0-9]/gi, '').toUpperCase();
            
            // Limit to 16 characters
            value = value.substring(0, 16);
            
            // Add dashes every 4 characters
            const formatted = value.match(/.{1,4}/g)?.join('-') || value;
            input.value = formatted;
        });
        
        // Allow Enter key to submit
        unlockCodeInput?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                unlockModalSubmit?.click();
            }
        });
    }
    
    /**
     * Setup event listeners for game state changes.
     * Demonstrates decoupled module communication via pub/sub.
     */
    setupStateListeners() {
        // Listen for completion count changes
        this.gameState.on('change:completedCount', (count: number) => {
            console.log(`Progress: ${count}/${this.gameState.get('totalPairs')} pairs completed`);
            this.completedCount = count;
        });
        
        // Listen for game completion
        this.gameState.on('complete', (data: any) => {
            console.log('Game complete!', data);
            
            // Record theme completion and check for rotation
            const rotationInfo = ThemeService.recordCompletion(this.isPremiumUser);
            
            if (rotationInfo.shouldRotate) {
                console.log('Rotating to next theme:', rotationInfo.nextTheme.name);
                this.currentTheme = rotationInfo.nextTheme;
                CONFIG.updateDimensions(this.currentTheme);
            }
            
            // Create confetti celebration
            if (this.dragLayer && this.stage) {
                AnimationEffects.createConfetti(this.stage, this.dragLayer);
                
                // Add floating emojis
                const emojis = ['🎉', '⭐', '🎊', '✨', '🎈', '🌟', '🎆', '🎇'];
                if (this.stage && this.dragLayer) {
                    const stageWidth = this.stage.width();
                    const stageHeight = this.stage.height();
                
                    emojis.forEach((emoji, index) => {
                        setTimeout(() => {
                            if (this.dragLayer) {
                                const x = stageWidth * (0.2 + index * 0.2);
                                const y = stageHeight * 0.5;
                                AnimationEffects.createFloatingEmoji(this.dragLayer, x, y, emoji);
                            }
                        }, index * 200);
                    });
                }
            }
            
            // Play celebration sound
            this.audioSystem.getSoundEffects().playCelebration();
            
            // Auto-reset after delay with fresh start (clear progress)
            setTimeout(() => this.resetGame(true), 3000);
        });
        
        // Listen for mute state changes
        this.gameState.on('change:isMuted', (isMuted: boolean) => {
            console.log('Mute state changed:', isMuted);
            this.updateMuteIcon();
        });
        
        // Listen for drag state changes
        this.gameState.on('change:isDragging', (isDragging: boolean) => {
            console.log('Dragging:', isDragging);
        });
    }
    
    /**
     * Setup Page Visibility API to pause/resume game when app goes to background/foreground.
     */
    setupVisibilityHandling() {
        var self = this;
        
        document.addEventListener('visibilitychange', function() {
            if (document.hidden) {
                // App going to background - pause game
                self.pauseGame();
            } else {
                // App coming to foreground - resume game
                self.resumeGame();
            }
        });
    }
    
    /**
     * Pause the entire game (stop animations, disable interactions).
     */
    pauseGame() {
        if (this.isPaused) return;
        
        console.log('Pausing game...');
        this.isPaused = true;
        
        // Stop all movement animations
        if (this.currentTheme?.hasMovement) {
            this.movementManager?.stopAll();
        }
        
        // Disable dragging on all draggables
        this.draggables.forEach((draggable: CustomNode) => {
            draggable.draggable(false);
        });
        
        // Stop any ongoing Konva animations using StageManager
        this.stageManager.stopDrag();
    }
    
    /**
     * Resume the game (restart animations, enable interactions).
     */
    resumeGame() {
        if (!this.isPaused) return;
        
        console.log('Resuming game...');
        this.isPaused = false;
        
        // Re-enable dragging on all draggables
        this.draggables.forEach((draggable: CustomNode) => {
            if (!draggable.isMatched) {
                draggable.draggable(true);
            }
        });
        
        // Restart movement animations if in moving shapes theme
        if (this.currentTheme?.hasMovement) {
            this.movementManager?.startAll();
        }
    }
    
    /**
     * Initialize the game - create stage, layers, shapes, and setup audio.
     * run only on page reload
     */
    init() {

        // Mount static UI components (must run before DebugService.init and setupPremiumUI
        // so their child element IDs are in the DOM when those methods query them)
        mountRotateOverlay();
        mountStartOverlay(
            () => this.handleStartGame(), 
            () => this.handleStartStories()
        );
        mountUnlockModal();
        mountMenuDropdown(); // must run before createHamburgerMenu (which may fire immediately if muted)

        // Initialize debug utilities
        DebugService.init();

        // Load current theme based on premium status
        this.currentTheme = ThemeService.getCurrentThemeFromStorage(this.isPremiumUser);
        CONFIG.updateDimensions(this.currentTheme);
        console.log('Current theme:', this.currentTheme.name);
        
        // Setup premium UI
        this.setupPremiumUI();
        
        // Initialize stage and layers using StageManager
        this.stageManager.init();
        this.stage = this.stageManager.getStage();
        this.staticLayer = this.stageManager.getStaticLayer();
        this.dragLayer = this.stageManager.getDragLayer();
        
        this.matchingPairs = GameUtils.setupMatchingPairs(this.isPremiumUser);
        
        // Initialize game state with pair count
        this.gameState.update('totalPairs', this.matchingPairs.length);
        
        this.targets = ShapeFactory.createTargets(this.matchingPairs, this.staticLayer!, this.currentTheme);
        this.draggables = ShapeFactory.createDraggables(this.matchingPairs, this.staticLayer!, this.dragHandlers, this.currentTheme);
        // ShapeFactory.createTitle(this.staticLayer!);
        this.feedbackText = ShapeFactory.createFeedbackText(this.staticLayer!);
        
        // Apply saved progress (hide completed pairs)
        this.applyProgress();
        
        // Initialize movement animation manager
        this.movementManager = new MovementAnimationManager(this.targets, this.draggables);
        
        // Start movement animation if theme supports it
        if (this.currentTheme.hasMovement) {
            this.movementManager.startAll();
        }
        
        // Prevent pull-to-refresh on iOS Safari
        this.preventPullToRefresh();

        // Setup audio system and mute button callback
        var self = this;
        this.audioSystem.getBackgroundMusic().setOnPlayCallback(function() {
            if (!self.hamburgerButton)
                self.createHamburgerMenu();
        });
        self.createThemeNameDisplay();
        this.audioSystem.init();
        
        // Setup start overlay for iOS audio compatibility
        this.setupStartOverlay();
        
        this.staticLayer!.batchDraw();
        this.dragLayer!.batchDraw();
    }
    
    /**
     * Setup the "Tap to Start" overlay for iOS audio compatibility.
     * Requires explicit user interaction to start audio on mobile devices.
     * If user has saved a muted preference, skip the overlay entirely.
     */
    /**
     * Handle "Start Game" button click from start overlay.
     * Starts background music and unlocks audio for iOS.
     */
    handleStartGame(evt?: Event) {
        const overlay = document.getElementById('startOverlay');
        const bgMusic = this.audioSystem.getBackgroundMusic();
        
        // Start audio from user interaction
        bgMusic.startFromUserInteraction();
        
        // Unlock sound effects for iOS (must be done from user gesture)
        if (evt) {
            this.audioSystem.getSoundEffects().tryUnlockFromEvent(evt as any);
        }
        
        // Hide overlay
        if (overlay) {
            overlay.classList.add('hidden');
        }
        
        // Create hamburger menu if not already created
        if (!this.hamburgerButton) {
            this.createHamburgerMenu();
        }
    }
    
    /**
     * Handle "Read Bedtime Stories" button click from start overlay.
     * Navigates to stories page and sets up UI.
     */
    handleStartStories(evt?: Event) {
        const overlay = document.getElementById('startOverlay');
        
        // Unlock audio for potential future sound effects in stories
        if (evt) {
            this.audioSystem.getSoundEffects().tryUnlockFromEvent(evt as any);
        }
        
        // Hide overlay
        if (overlay) {
            overlay.classList.add('hidden');
        }
        
        // Create hamburger menu if not already created
        if (!this.hamburgerButton) {
            this.createHamburgerMenu();
        }
        
        // Navigate to stories page
        if ((window as any).__appNavigate) {
            (window as any).__appNavigate('/stories');
        }
    }
    
    /**
     * Setup start overlay - checks if user has muted preference and skips overlay if so.
     */
    setupStartOverlay() {
        var overlay = document.getElementById('startOverlay');
        if (!overlay) return;
        
        // If user has saved muted preference, hide overlay and create menu immediately
        const bgMusic = this.audioSystem.getBackgroundMusic();
        if (bgMusic.isMuted()) {
            overlay.classList.add('hidden');
            this.createHamburgerMenu();
            return;
        }
    }
    
    /**
     * Prevent pull-to-refresh on iOS Safari and other touch-based browsers.
     * Prevents the page from scrolling when dragging outside game elements.
     */
    preventPullToRefresh() {
        // Prevent touchmove on document to stop pull-to-refresh
        document.addEventListener('touchmove', function(e) {
            // Allow touch events on the canvas itself
            const target = e.target as HTMLElement;
            if (target && target.tagName === 'CANVAS') {
                return;
            }
            // Allow scrolling inside pages that need it (story list, story viewer, faq)
            if (target && target.closest('.page[id]')) {
                return;
            }
            // Prevent default for everything else to stop pull-to-refresh
            e.preventDefault();
        }, { passive: false });
        
        // Also prevent on the body, but allow scrollable page children
        document.body.addEventListener('touchmove', function(e) {
            const target = e.target as HTMLElement;
            if (target && target.closest('.page[id]')) {
                return;
            }
            e.preventDefault();
        }, { passive: false });
        
        // Prevent touchstart default on body to avoid iOS Safari gestures
        document.body.addEventListener('touchstart', function(e) {
            const target = e.target as HTMLElement;
            if (target && target.tagName === 'CANVAS') {
                return;
            }
            // Allow touches inside scrollable pages
            if (target && target.closest('.page[id]')) {
                return;
            }
            e.preventDefault();
        }, { passive: false });
        
        // Prevent double-tap zoom on iOS (old Safari versions)
        let lastTouchEnd = 0;
        document.addEventListener('touchend', function(e) {
            const now = Date.now();
            if (now - lastTouchEnd <= 300) {
                e.preventDefault();
            }
            lastTouchEnd = now;
        }, { passive: false });
        
        // Prevent pinch-to-zoom gestures on old iOS
        document.addEventListener('gesturestart', function(e) {
            e.preventDefault();
        }, { passive: false });
        
        document.addEventListener('gesturechange', function(e) {
            e.preventDefault();
        }, { passive: false });
        
        document.addEventListener('gestureend', function(e) {
            e.preventDefault();
        }, { passive: false });
    }
    
    /**
     * Setup window resize handler with debouncing.
     * Recalculates CONFIG dimensions and resets game when screen size changes.
     */
    setupResizeHandler() {
        var self = this;
        window.addEventListener('resize', function() {
            // Clear existing timeout
            if (self.resizeTimeout) {
                clearTimeout(self.resizeTimeout);
            }
            
            // Debounce resize - wait 500ms after last resize event
            self.resizeTimeout = setTimeout(function() {
                console.log('Window resized - recalculating layout');
                
                // Update CONFIG dimensions with current theme's numPairs
                CONFIG.updateDimensions(self.currentTheme!);
                
                // Resize stage
                if (self.stage) {
                    self.stage.width(CONFIG.WIDTH);
                    self.stage.height(CONFIG.HEIGHT);
                }
                
                // Reset game with new dimensions
                self.resetGame();
            }, 500);
        });
    }
    
    /**
     * Create hamburger menu button and dropdown.
     * Menu contains: mute button, buy themes, restore purchase.
     */
    createHamburgerMenu() {
        if (!CONFIG.UI.MUTE_BUTTON.ENABLED) return;
        
        // Get hamburger button
        this.hamburgerButton = document.getElementById('hamburgerButton');
        if (!this.hamburgerButton) return;
        
        // Get menu dropdown
        this.menuDropdown = document.getElementById('menuDropdown');
        if (!this.menuDropdown) return;
        
        // Get mute button inside menu
        this.muteButton = document.getElementById('muteButton');
        if (!this.muteButton) return;
        
        // Get the icon element inside the mute button
        this.muteIcon = this.muteButton.querySelector('.material-icons');
        if (!this.muteIcon) return;
        
        // Show the hamburger button
        this.hamburgerButton.classList.remove('hidden');
        
        // Set initial mute icon
        this.updateMuteIcon();
        
        // Initialize dark theme
        this.initializeDarkTheme();
        
        // Remove old event listeners before adding new ones to prevent duplicates
        if (this.toggleMenuHandler) {
            removeClickAndTouch(this.hamburgerButton, this.toggleMenuHandler);
        }
        if (this.toggleMuteHandler) {
            removeClickAndTouch(this.muteButton, this.toggleMuteHandler);
        }
        
        // Hamburger button toggles menu
        var self = this;
        this.toggleMenuHandler = function(evt: any) {
            self.toggleMenu();
            
            if (evt && evt.preventDefault) {
                evt.preventDefault();
            }
        };
        
        addClickAndTouch(this.hamburgerButton, this.toggleMenuHandler);
        
        // Mute button handler
        this.toggleMuteHandler = function(evt: any) {
            const isMuted = self.audioSystem.toggleBackgroundMute();
            self.gameState.update('isMuted', isMuted);
            self.updateMuteIcon();
            
            if (evt && evt.preventDefault) {
                evt.preventDefault();
            }
        };
        
        addClickAndTouch(this.muteButton, this.toggleMuteHandler);
        
        // Close menu when clicking outside (only add once)
        // NOTE: intentionally using document.addEventListener('click') instead of addClickAndTouch()
        // because addClickAndTouch() calls preventDefault() on touchend, which would break
        // scrolling, text selection, and other touch interactions globally on the document.
        if (!this.hamburgerButton.dataset.outsideListenerAdded) {
            document.addEventListener('click', (e) => {
                if (this.menuDropdown?.classList.contains('show')) {
                    const target = e.target as HTMLElement;
                    if (!this.hamburgerButton?.contains(target) && !this.menuDropdown?.contains(target)) {
                        this.closeMenu();
                    }
                }
            });
            this.hamburgerButton.dataset.outsideListenerAdded = 'true';
        }
    }
    
    /**
     * Initialize dark theme toggle button and load saved preference.
     */
    initializeDarkTheme() {
        // Get dark theme button
        this.darkThemeButton = document.getElementById('darkThemeButton');
        if (!this.darkThemeButton) return;
        
        // Show the button
        this.darkThemeButton.classList.remove('hidden');
        
        // Check localStorage for saved theme preference
        const savedTheme = localStorage.getItem('toddlerGame_darkTheme');
        this.isDarkTheme = savedTheme === 'true';
        
        // Apply saved theme
        if (this.isDarkTheme) {
            document.body.classList.add('dark-theme');
            const icon = this.darkThemeButton.querySelector('.material-icons');
            if (icon) icon.textContent = 'light_mode';
        }
        
        // Add click handler
        var self = this;
        if (this.toggleDarkThemeHandler) {
            removeClickAndTouch(this.darkThemeButton, this.toggleDarkThemeHandler);
        }
        this.toggleDarkThemeHandler = function(evt: any) {
            self.toggleDarkTheme();
            if (evt && evt.preventDefault) {
                evt.preventDefault();
            }
        };
        
        addClickAndTouch(this.darkThemeButton, this.toggleDarkThemeHandler);
    }
    
    /**
     * Toggle dark theme and save preference.
     */
    toggleDarkTheme() {
        this.isDarkTheme = !this.isDarkTheme;
        
        if (this.isDarkTheme) {
            document.body.classList.add('dark-theme');
            const icon = this.darkThemeButton?.querySelector('.material-icons');
            if (icon) icon.textContent = 'light_mode';
        } else {
            document.body.classList.remove('dark-theme');
            const icon = this.darkThemeButton?.querySelector('.material-icons');
            if (icon) icon.textContent = 'dark_mode';
        }
        
        // Save preference
        localStorage.setItem('toddlerGame_darkTheme', this.isDarkTheme.toString());
    }
    
    /**
     * Toggle menu dropdown visibility.
     */
    toggleMenu() {
        this.menuDropdown?.classList.toggle('show');
    }
    
    /**
     * Close menu dropdown.
     */
    closeMenu() {
        this.menuDropdown?.classList.remove('show');
    }
    
    /**
     * Create theme name display at the top of the screen.
     * Secret: tap 5 times rapidly to toggle debug buttons.
     */
    createThemeNameDisplay() {
        this.themeNameElement = document.getElementById('themeName');
        if (this.themeNameElement && this.currentTheme) {
            this.themeNameElement.textContent = themeNames[this.currentTheme.id] ?? this.currentTheme.name;
            this.themeNameElement.style.display = 'block';

            // Secret 5-tap trigger to show/hide debug buttons
            let tapCount = 0;
            let tapTimer: any = null;
            const secretTapHandler = (evt: Event) => {
                evt.stopPropagation();
                tapCount++;
                clearTimeout(tapTimer);
                if (tapCount >= 5) {
                    tapCount = 0;
                    DebugService.toggleAllDebugButtons();
                } else {
                    tapTimer = setTimeout(() => { tapCount = 0; }, 800);
                }
            };
            this.themeNameElement.addEventListener('click', secretTapHandler);
            this.themeNameElement.addEventListener('touchend', secretTapHandler);
        }
    }
    
    /**
     * Update theme name display when theme changes.
     */
    updateThemeNameDisplay() {
        if (this.themeNameElement && this.currentTheme) {
            this.themeNameElement.textContent = themeNames[this.currentTheme.id] ?? this.currentTheme.name;
        }
    }
    
    /**
     * Update mute button icon based on current mute state.
     */
    updateMuteIcon() {
        if (!this.muteIcon) return;
        
        // Update icon: music_note when unmuted, music_off when muted
        this.muteIcon.textContent = this.audioSystem.isBackgroundMusicMuted() ? 'music_off' : 'music_note';
    }
    
    /**
     * Apply saved progress - hide/snap completed pairs to their targets.
     * Called after shapes are created to restore the visual state.
     */
    applyProgress() {
        let completedCount = 0;
        const isSequential = this.currentTheme?.isSequential;
        
        this.matchingPairs.forEach((pair: MatchingPair) => {
            if (pair.completed) {
                const draggable = this.draggables[pair.dsIndex];
                const target = this.targets.find((t: CustomNode) => t.tsIndex === pair.tsIndex);
                
                if (draggable && target) {
                    if (isSequential) {
                        // For sequential themes: destroy both draggable and target
                        draggable.destroy();
                        if (draggable.numberText) {
                            draggable.numberText.destroy();
                        }
                        target.destroy();
                    } else {
                        // For non-sequential themes: snap draggable to target position
                        draggable.position({ x: target.x(), y: target.y() });
                        if (draggable.numberText) {
                            draggable.numberText.position(draggable.position());
                        }
                        
                        // Mark as matched to prevent movement
                        draggable.isMatched = true;
                        
                        // Disable dragging
                        draggable.draggable(false);
                        
                        // Hide target
                        target.opacity(0);
                    }
                    
                    completedCount++;
                }
            }
        });
        
        // For sequential themes, show the next unmatched target
        if (isSequential && completedCount > 0) {
            // Find the first unmatched pair by tsIndex order
            const unmatchedPairs = this.matchingPairs
                .filter((p: MatchingPair) => !p.completed)
                .sort((a: MatchingPair, b: MatchingPair) => a.tsIndex - b.tsIndex);
            
            if (unmatchedPairs.length > 0) {
                const nextPair = unmatchedPairs[0];
                const nextTarget = this.targets.find((t: CustomNode) => t.tsIndex === nextPair.tsIndex);
                
                if (nextTarget) {
                    nextTarget.visible(true);
                    nextTarget.isHidden = false;
                    nextTarget.opacity(0.6);
                    console.log(`[SequentialTheme] Showing next unmatched target tsIndex=${nextPair.tsIndex}`);
                }
            }
        }
        
        // Update game state with completed count
        if (completedCount > 0) {
            this.completedCount = completedCount;
            this.gameState.update('completedCount', completedCount);
            console.log(`Restored progress: ${completedCount}/${this.matchingPairs.length} pairs completed`);
        }
    }
    
    /**
     * Reset the game to initial state.
     * @param {boolean} clearProgress - If true, clears saved configuration and generates new game
     */
    resetGame(clearProgress: boolean = false) {
        // Clear progress and configuration if requested (e.g., after completing all pairs)
        if (clearProgress) {
            GameUtils.clearSavedConfiguration(); // This clears both progress and configuration
        }
        
        // Reload current theme (in case it changed due to rotation)
        this.currentTheme = ThemeService.getCurrentThemeFromStorage(this.isPremiumUser);
        this.updateThemeNameDisplay();
        CONFIG.updateDimensions(this.currentTheme);
        console.log('Resetting game with theme:', this.currentTheme.name);
        
        // Clear both layers using StageManager
        this.stageManager.clearLayers();
        
        // Reset counters and arrays
        this.completedCount = 0;
        this.targets = [];
        this.draggables = [];
        this.matchingPairs = [];
        // Don't reset muteButton/muteIcon - they're HTML elements that persist
        
        // Recreate everything (will generate new config if cleared, or load from localStorage)
        this.matchingPairs = GameUtils.setupMatchingPairs(this.isPremiumUser);
        
        // Reset game state
        this.gameState.reset(this.matchingPairs.length);
        
        this.targets = ShapeFactory.createTargets(this.matchingPairs, this.staticLayer!, this.currentTheme!);
        this.draggables = ShapeFactory.createDraggables(this.matchingPairs, this.staticLayer!, this.dragHandlers, this.currentTheme!);
        //ShapeFactory.createTitle(this.staticLayer!);
        this.feedbackText = ShapeFactory.createFeedbackText(this.staticLayer!);
        
        // Apply saved progress (if any)
        this.applyProgress();
        
        // Recreate movement manager with new shapes
        this.movementManager = new MovementAnimationManager(this.targets, this.draggables);
        
        // Start movement animation if theme supports it
        if (this.currentTheme?.hasMovement) {
            this.movementManager.startAll();
        }
        
        // Always recreate the hamburger menu after reset
        this.createHamburgerMenu();
        
        this.staticLayer!.batchDraw();
        this.dragLayer!.batchDraw();
    }
}

// Initialize the game when the page loads
window.addEventListener('DOMContentLoaded', () => {
    try {
        // localStorage.clear();
        const game = ToddlerGame.getInstance();

        // Expose EmojiCompatibility globally for debugging on devices
        // ToddlerGame.game.EmojiTest = {
        //     check: (emoji: string) => EmojiCompatibility.isEmojiSupported(emoji),
        //     checkAll: (emojis: string[]) => EmojiCompatibility.debugEmojiSupport(emojis),
        //     showVisual: (emojis: string[]) => EmojiCompatibility.createVisualTest(emojis),
        //     clearCache: () => EmojiCompatibility.clearCache(),
        //     testAnimals: () => EmojiCompatibility.createVisualTest(allLabels.animals),
        //     testFruits: () => EmojiCompatibility.createVisualTest(allLabels.fruits),
        //     testVehicles: () => EmojiCompatibility.createVisualTest(allLabels.vehicles),
        //     testAll: () => {
        //         const all = [
        //             ...allLabels.animals,
        //             ...allLabels.fruits,
        //             ...allLabels.vehicles,
        //             ...allLabels.food,
        //             ...allLabels.faces,
        //             ...allLabels.bodyParts,
        //             ...allLabels.clothing,
        //             ...allLabels.sports,
        //             ...allLabels.nature,
        //             ...allLabels.weather,
        //             ...allLabels.music
        //         ];
        //         EmojiCompatibility.createVisualTest(all);
        //     }
        // };
        
        // console.log('🧪 Emoji testing available! Use EmojiTest.testAnimals(), EmojiTest.testAll(), etc.');
        
        // Setup routing
        setupRouting(game);
    } catch (error) {

        alert('An error occurred while initializing the game. Please try reloading the page.');
        console.error('Failed to initialize game:', error);
        
        // Display error on screen
        const errorDisplay = document.getElementById('debugErrorDisplay');
        if (errorDisplay) {
            errorDisplay.classList.add('visible');
            const errorDiv = document.createElement('div');
            errorDiv.className = 'error-item';
            errorDiv.textContent = `INIT ERROR: ${error instanceof Error ? error.message : String(error)}\n\nStack: ${error instanceof Error ? error.stack : 'No stack trace'}`;
            errorDisplay.appendChild(errorDiv);
        }
        
        // Also show alert as fallback
        alert(`Game failed to start:\n${error instanceof Error ? error.message : String(error)}`);
    }
});

// Hot Module Replacement: full page reload on any module change.
// Partial HMR cannot re-initialize a live canvas game, so a full reload is the only correct behaviour.
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        import.meta.hot!.invalidate();
    });
}
